import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  m as motion,
  AnimatePresence,
  LazyMotion,
  useReducedMotion,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Search,
  Sparkles,
  MousePointerClick,
  Cpu,
  Bot,
  Palette,
  ShoppingBag,
  Code2,
  ChevronDown,
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import styles from "./Navigation.module.css";

/* The header is on every page, so it renders the lightweight `m`
   components and pulls Framer's animation and layout features in after
   first paint (~25 KB gzipped off the critical path). Until they land
   the header is fully usable; the chip and menus just appear without
   their transition. */
const loadMotionFeatures = () =>
  import("./motionFeatures").then((mod) => mod.default);

/* The header has exactly two states and one authored transition
   between them.

   At rest it is not a bar at all — no fill, no blur, no edge. Nothing
   is scrolling underneath it yet, so a glass pane there would be
   decoration, and the hero reads better without a lid on it.

   Once content passes beneath, the pane materialises and the glass is
   doing real work: separating the controls from whatever is sliding
   under them. Height never changes between the two, so the page below
   never reflows — only paint properties cross-fade. */
const SCROLL_TRIGGER = 24;

const EASE_EXPO = [0.16, 1, 0.3, 1];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  /* Which nav item the sliding chip is currently under. null means
     "nothing hovered" — the chip goes home to the active route. */
  const [hoveredPath, setHoveredPath] = useState(null);
  const location = useLocation();
  const studioPath = location.pathname.replace(/^\/es(?=\/|$)/, "") || "/";
  const isStudioHome = !studioPath.startsWith("/admin");
  const { t, i18n } = useTranslation("common");
  const closeTimer = useRef(null);
  const servicesRef = useRef(null);
  const mobileMenuRef = useRef(null);
  /* CSS media queries can't reach Framer's JS-driven transitions, so
     the preference has to be read here too. */
  const prefersReducedMotion = useReducedMotion();
  const motionDuration = (seconds) => (prefersReducedMotion ? 0 : seconds);

  /* startsWith, not ===: a locale that resolves to es-MX or es-ES must
     still get the /es prefix. Footer and LanguageSwitcher test it the
     same way; if these disagree the header and footer would link to
     different locales on the same page. */
  const langPrefix = i18n.language?.startsWith("es") ? "/es" : "";

  const localePath = (path) => {
    if (path === "/") return langPrefix || "/";
    return `${langPrefix}${path}`;
  };

  const isActivePath = (path) => {
    const currentPath = location.pathname;
    if (path === "/services") {
      return currentPath.includes("/services");
    }
    return currentPath === localePath(path);
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_TRIGGER);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
    /* Without this the chip stays parked on whatever was hovered when
       the click happened, instead of settling onto the page you just
       landed on. */
    setHoveredPath(null);
  }, [location]);

  /* Opened by click or tap, the panel has no pointer to leave — so it
     needs an explicit outside dismiss. Focus counts as "outside" too,
     or tabbing past the panel would leave it hanging open. */
  useEffect(() => {
    if (!isServicesOpen) return undefined;
    const onOutside = (event) => {
      if (!servicesRef.current?.contains(event.target)) {
        clearTimeout(closeTimer.current);
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("pointerdown", onOutside);
    document.addEventListener("focusin", onOutside);
    return () => {
      document.removeEventListener("pointerdown", onOutside);
      document.removeEventListener("focusin", onOutside);
    };
  }, [isServicesOpen]);

  /* The mobile overlay covers the page, so the page behind it must not
     scroll under it. Restore on close and on unmount. */
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileMenuOpen]);

  /* The overlay declares aria-modal, so focus has to actually behave
     modally: move into the sheet on open, cycle inside it on Tab, and
     go back to the trigger on close. Declaring the role without this
     is worse than not declaring it — it tells a screen reader the rest
     of the page is inert when it is still reachable. */
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const sheet = mobileMenuRef.current;
    if (!sheet) return undefined;

    const opener = document.activeElement;
    const focusables = () =>
      sheet.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

    focusables()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    sheet.addEventListener("keydown", onKeyDown);
    return () => {
      sheet.removeEventListener("keydown", onKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [isMobileMenuOpen]);

  /* Escape closes whichever layer is open, outermost first. */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
      else if (isServicesOpen) setIsServicesOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobileMenuOpen, isServicesOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* Pointer travelling from the trigger to the panel crosses an 8px
     gap. A short close delay keeps the panel alive across it without
     needing an invisible hit-area bridge. */
  const openServices = () => {
    clearTimeout(closeTimer.current);
    setIsServicesOpen(true);
  };
  const closeServices = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsServicesOpen(false), 120);
  };

  const services = [
    {
      path: "/services/ai-implementation",
      label: "AI Implementation",
      icon: Cpu,
      description: t("serviceDescriptions.aiImplementation"),
    },
    {
      path: "/services/ai-agents",
      label: "AI Agents",
      icon: Bot,
      description: t("serviceDescriptions.aiAgents"),
    },
    {
      path: "/services/seo",
      label: "SEO",
      icon: Search,
      description: t("serviceDescriptions.seo"),
    },
    {
      path: "/services/geo",
      label: "GEO",
      icon: Sparkles,
      description: t("serviceDescriptions.geo"),
    },
    {
      path: "/services/cro",
      label: "CRO",
      icon: MousePointerClick,
      description: t("serviceDescriptions.cro"),
    },
    {
      path: "/services/shopify",
      label: "Shopify",
      icon: ShoppingBag,
      description: t("serviceDescriptions.shopify"),
    },
    {
      path: "/services/saas",
      label: "SaaS & Apps",
      icon: Code2,
      description: t("serviceDescriptions.saas"),
    },
    {
      path: "/services/creative-engine",
      label: "Creative Engine",
      icon: Palette,
      description: t("serviceDescriptions.creativeEngine"),
    },
  ];

  /* Order is capability → proof → proof → identity → thinking, so
     commercial intent falls off left to right. Services keeps slot 1:
     it carries the dropdown and the eight revenue pages. Blog goes last
     — it earns attention, it doesn't convert. */
  const navLinks = [
    { path: "/services", label: t("nav.services"), hasDropdown: true },
    { path: "/projects", label: t("nav.projects") },
    { path: "/clients", label: t("nav.clients") },
    { path: "/about", label: t("nav.about") },
    { path: "/blog", label: t("nav.blog") },
  ];

  /* The chip sits under whatever is hovered; with nothing hovered it
     travels back to the current route and stays there as the "you are
     here" marker. One element, shared via layoutId, so Framer
     interpolates its position with a transform. */
  const activeLink = navLinks.find((link) => isActivePath(link.path));
  const chipPath = hoveredPath ?? activeLink?.path ?? null;

  return (
    <LazyMotion features={loadMotionFeatures}>
      <header
        className={`${styles.nav} ${isScrolled ? styles.scrolled : ""} ${isStudioHome ? styles.studioNav : ""}`}
        data-scrolled={isScrolled ? "true" : "false"}
      >
        <nav
          className={styles.navContainer}
          aria-label={t("accessibility.mainNavigation", "Main")}
        >
          {/* alt carries the name; the link has no aria-label of its own,
              so the mark is announced once rather than twice. */}
          <Link to={localePath("/")} className={styles.logo}>
            <img
              src="/images/logos/logo-jamak.svg"
              alt="JAMAK — home"
              width="91"
              height="30"
              className={styles.logoImage}
            />
          </Link>

          <div
            className={styles.navLinks}
            onMouseLeave={() => setHoveredPath(null)}
          >
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.path}
                  ref={servicesRef}
                  className={styles.dropdownContainer}
                  onMouseEnter={() => {
                    openServices();
                    setHoveredPath(link.path);
                  }}
                  onMouseLeave={closeServices}
                >
                  <button
                    type="button"
                    className={`${styles.navLink} ${
                      isActivePath("/services") ? styles.active : ""
                    }`}
                    onClick={() =>
                      isServicesOpen ? closeServices() : openServices()
                    }
                    onFocus={() => setHoveredPath(link.path)}
                    onBlur={() => setHoveredPath(null)}
                    aria-expanded={isServicesOpen}
                    aria-controls="services-menu"
                    aria-haspopup="true"
                  >
                    {chipPath === link.path && (
                      <motion.span
                        layoutId="nav-chip"
                        className={styles.navChip}
                        transition={{
                          duration: motionDuration(0.25),
                          ease: EASE_EXPO,
                        }}
                      />
                    )}
                    <span className={styles.navLabel}>{link.label}</span>
                    <ChevronDown
                      size={14}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className={`${styles.chevron} ${isServicesOpen ? styles.chevronOpen : ""}`}
                    />
                    {isActivePath("/services") && (
                      <span className={styles.activeDot} aria-hidden="true" />
                    )}
                  </button>

                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        id="services-menu"
                        className={styles.dropdown}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                          opacity: 0,
                          y: -4,
                          transition: {
                            duration: motionDuration(0.12),
                            ease: "linear",
                          },
                        }}
                        transition={{
                          duration: motionDuration(0.2),
                          ease: EASE_EXPO,
                        }}
                        role="menu"
                        aria-label={t("accessibility.servicesSubmenu")}
                      >
                        <Link
                          to={localePath("/services")}
                          className={styles.dropdownAll}
                          role="menuitem"
                        >
                          {t("nav.allServices")}
                        </Link>
                        <div
                          className={styles.dropdownDivider}
                          role="separator"
                        />
                        {services.map((service) => {
                          const Icon = service.icon;
                          return (
                            <Link
                              key={service.path}
                              to={localePath(service.path)}
                              role="menuitem"
                              className={`${styles.dropdownItem} ${
                                isActivePath(service.path) ? styles.active : ""
                              }`}
                            >
                              <span
                                className={styles.dropdownIcon}
                                aria-hidden="true"
                              >
                                <Icon size={18} strokeWidth={1.75} />
                              </span>
                              <span className={styles.dropdownText}>
                                <span className={styles.dropdownItemLabel}>
                                  {service.label}
                                </span>
                                <span
                                  className={styles.dropdownItemDescription}
                                >
                                  {service.description}
                                </span>
                              </span>
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={localePath(link.path)}
                  className={`${styles.navLink} ${isActivePath(link.path) ? styles.active : ""}`}
                  onMouseEnter={() => setHoveredPath(link.path)}
                  onFocus={() => setHoveredPath(link.path)}
                  onBlur={() => setHoveredPath(null)}
                  aria-current={isActivePath(link.path) ? "page" : undefined}
                >
                  {chipPath === link.path && (
                    <motion.span
                      layoutId="nav-chip"
                      className={styles.navChip}
                      transition={{
                        duration: motionDuration(0.25),
                        ease: EASE_EXPO,
                      }}
                    />
                  )}
                  <span className={styles.navLabel}>{link.label}</span>
                  {isActivePath(link.path) && (
                    <span className={styles.activeDot} aria-hidden="true" />
                  )}
                </Link>
              ),
            )}
          </div>

          <div className={styles.navActions}>
            <span className={styles.rule} aria-hidden="true" />
            <LanguageSwitcher />
            <Link to={localePath("/quote")} className={styles.cta}>
              {t("nav.quote")}
            </Link>
          </div>

          <button
            type="button"
            className={styles.mobileMenuToggle}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={
              isMobileMenuOpen
                ? t("accessibility.closeMenu")
                : t("accessibility.openMenu")
            }
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {/* Two rules that rotate into a cross. Transforms only — no
                width, height or margin is animated. */}
            <span
              className={`${styles.bar} ${isMobileMenuOpen ? styles.barTopOpen : ""}`}
            />
            <span
              className={`${styles.bar} ${isMobileMenuOpen ? styles.barBottomOpen : ""}`}
            />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            ref={mobileMenuRef}
            className={styles.mobileMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: motionDuration(0.2), ease: "linear" }}
            role="dialog"
            aria-modal="true"
            aria-label={t("accessibility.mobileNavigation")}
          >
            <div className={styles.mobileMenuScroll}>
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: motionDuration(0.24),
                    ease: EASE_EXPO,
                    delay: motionDuration(0.03 * index),
                  }}
                >
                  {link.hasDropdown ? (
                    <>
                      <button
                        type="button"
                        className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger} ${
                          isActivePath("/services") ? styles.active : ""
                        }`}
                        onClick={() => setIsMobileServicesOpen((open) => !open)}
                        aria-expanded={isMobileServicesOpen}
                        aria-controls="mobile-services"
                      >
                        {link.label}
                        <ChevronDown
                          size={20}
                          strokeWidth={1.75}
                          aria-hidden="true"
                          className={`${styles.chevron} ${isMobileServicesOpen ? styles.chevronOpen : ""}`}
                        />
                      </button>
                      {/* Grid-rows collapse: animatable without touching
                          height, padding or margin. */}
                      <div
                        id="mobile-services"
                        className={`${styles.mobileDropdown} ${
                          isMobileServicesOpen ? styles.mobileDropdownOpen : ""
                        }`}
                      >
                        <div className={styles.mobileDropdownInner}>
                          <Link
                            to={localePath("/services")}
                            className={styles.mobileDropdownItem}
                          >
                            {t("nav.allServices")}
                          </Link>
                          {services.map((service) => (
                            <Link
                              key={service.path}
                              to={localePath(service.path)}
                              className={`${styles.mobileDropdownItem} ${
                                isActivePath(service.path) ? styles.active : ""
                              }`}
                            >
                              {service.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={localePath(link.path)}
                      className={`${styles.mobileNavLink} ${isActivePath(link.path) ? styles.active : ""}`}
                      aria-current={
                        isActivePath(link.path) ? "page" : undefined
                      }
                    >
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            <div className={styles.mobileFooter}>
              <LanguageSwitcher />
              <Link
                to={localePath("/quote")}
                className={`${styles.cta} ${styles.ctaFull}`}
              >
                {t("nav.quote")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};

export default Navigation;
